/* Handles the automatic redirect to the partner site after the `redirectTimeout` period. The navigation will
redirect the user to the `destinationUrl`. This can happen in one of three ways:
1. Using a window.location.href redirect
2. Using the cj.navigate method if the domain is a CJ publisher domain
3. Using a meta tag redirect if the user has Javascript disabled in their browser

If the page should not be automatically redirected, do not include this component in the page. Instead,
the practice has been to include the `destinationUrl` in the DisplayComponent as a link or button for a 
manual user redirect.
*/

'use client';

import Script from 'next/script';
import React from 'react';
import { Logger } from '../../../logging';
import { datadogLogs } from '../../datadog-logs';
import { useIsClient } from '../isClientContext';
import FailedToNavigateViaCJScriptError from './FailedToNavigateViaCJScript';

const LOGGER_NAME = 'RedirectComponent';
const REDIRECT_TIME_EXCEEDED_WARNING_TIMEOUT = 3000;

interface Props {
  // time before starting redirecting to partner site
  redirectWaitTime: number;
  destinationUrl: string;
  isCJPublisherDomain: boolean;
  requestId: string;
  shoppingFinishedId: string;
  uuid: string;
}

const logger = Logger.logger();

const logCJScriptError = (e: Error, redirectContext: object) => {
  const messageContext = {
    error: e?.name,
    message: e?.message,
    ...redirectContext,
  };
  logger.warn(
    LOGGER_NAME,
    `${e?.name}: Falling back to normal redirect`,
    messageContext
  );
  datadogLogs.logger.warn(`${e?.name}: Falling back to normal redirect`, {
    context: messageContext,
  });
};

const logRedirectStartAndTimeoutMessage = (redirectContext: object) => {
  // This should log once per automatic redirect initiated
  const redirectStartedMessage = 'Starting redirect to partner site';
  logger.info(LOGGER_NAME, redirectStartedMessage, redirectContext);
  datadogLogs.logger.info(`RedirectStarted: ${redirectStartedMessage}`, {
    context: redirectContext,
  });

  // If still on the page after the REDIRECT_TIME_EXCEEDED_WARNING_TIMEOUT, log a warning to
  // Datadog logs. This is to capture redirects that hang on the interstitial page. We keep
  // an eye on the rate of how many redirects started above to how many exceed the
  // REDIRECT_TIME_EXCEEDED_WARNING_TIMEOUT time in light of prod impacts we've encountered:
  // - https://nerdwallet.atlassian.net/browse/PROD-1059
  // - https://nerdwallet.atlassian.net/browse/PROD-1030
  setTimeout(() => {
    const exceededTimeoutMessage = 'Redirect to partner site exceeded timeout';
    logger.warn(LOGGER_NAME, exceededTimeoutMessage, redirectContext);
    datadogLogs.logger.info(
      `RedirectTimeoutExceededWarning: ${exceededTimeoutMessage}`,
      {
        context: redirectContext,
      }
    );
  }, REDIRECT_TIME_EXCEEDED_WARNING_TIMEOUT);
};

const RedirectComponent: React.FC<Props> = ({
  redirectWaitTime,
  destinationUrl,
  isCJPublisherDomain,
  requestId,
  shoppingFinishedId,
  uuid,
}) => {
  const isClient = useIsClient();

  const redirectContext: object = {
    destination_url: destinationUrl,
    is_cj_publisher_domain: isCJPublisherDomain,
    redirect_wait_time: redirectWaitTime,
    request_id: requestId,
    shopping_finished_id: shoppingFinishedId,
    uuid,
  };

  // Performs a normal redirect if the domain is not a CJ publisher domain
  if (isClient && !isCJPublisherDomain) {
    setTimeout(() => {
      logRedirectStartAndTimeoutMessage(redirectContext);
      window.parent.location.href = destinationUrl;
    }, redirectWaitTime);
  }

  /* If the domain is a CJ publisher domain, we use the the CJ script to redirect the user.
    We include a meta tag redirect for users with Javascript disabled; this includes both CJ and non-CJ domains.
  */
  return (
    <>
      <header>
        <noscript>
          <meta httpEquiv="refresh" content={`1; url=${destinationUrl}`} />
        </noscript>
      </header>
      <div>
        {isClient && isCJPublisherDomain && (
          <Script
            src="https://www.p.zjptg.com/tag/2929746/100001"
            strategy="afterInteractive"
            data-testid="cj-script"
            onLoad={() => {
              setTimeout(() => {
                logRedirectStartAndTimeoutMessage(redirectContext);
                try {
                  window.cj.navigate(destinationUrl);
                } catch {
                  // Fallback to using normal redirects if the CJ script fails to execute
                  // https://nerdwallet.atlassian.net/browse/PP-6968
                  logCJScriptError(
                    new FailedToNavigateViaCJScriptError(
                      'Failed to execute CJ script'
                    ),
                    redirectContext
                  );

                  window.parent.location.href = destinationUrl;
                }
              }, redirectWaitTime);
            }}
            // Fallback to using normal redirects if the CJ script fails to load
            // https://nerdwallet.atlassian.net/browse/PROD-1059
            onError={() => {
              logCJScriptError(
                new FailedToNavigateViaCJScriptError(
                  'Failed to load CJ script'
                ),
                redirectContext
              );
              setTimeout(() => {
                logRedirectStartAndTimeoutMessage(redirectContext);
                window.parent.location.href = destinationUrl;
              }, redirectWaitTime);
            }}
          />
        )}
      </div>
    </>
  );
};

export default RedirectComponent;
